import React, { useEffect, useState } from "react";
import { friendlyError } from "../../../middleware/common-functions";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_PRODUCTION_RECIPES,
  GET_BRANCHES,
  GET_INGREDIENT_STOCK,
  START_PRODUCTION_BATCH,
} from "./queries";
import { ProductionRecipe, ProductionRecipeInputType } from "./models";

const StartBatch: React.FC = () => {
  const client = useApolloClient();
  const history = useHistory();
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [notes, setNotes] = useState("");
  const [stockLevels, setStockLevels] = useState<
    Record<string, number>
  >({});

  const [fetchRecipes, { data: recipeData, loading: loadingRecipes }] =
    useLazyQuery(GET_PRODUCTION_RECIPES, { fetchPolicy: "no-cache" });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  const [startBatch, { loading: starting }] = useMutation(
    START_PRODUCTION_BATCH,
    {
      onCompleted: (data) => {
        toast.success(
          lang("Producción iniciada", "Production started")
        );
        history.push(`/inventory/production/${data.startProductionBatch.id}`);
      },
      onError: (err) => toast.error(friendlyError(err)),
    }
  );

  useEffect(() => {
    fetchRecipes({ variables: { take: 200 } });
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  const selectedRecipe: ProductionRecipe | null =
    recipeData?.getProductionRecipes?.find(
      (r: ProductionRecipe) => r.id === selectedRecipeId
    ) || null;

  // Fetch stock for each input when recipe + branch selected
  useEffect(() => {
    if (selectedRecipe && branchId) {
      const newLevels: Record<string, number> = {};
      const promises = selectedRecipe.inputs.map(
        async (inp: ProductionRecipeInputType) => {
          try {
            const { data } = await client.query({
              query: GET_INGREDIENT_STOCK,
              fetchPolicy: "no-cache",
              variables: {
                ingredient_id: inp.ingredient_id.toString(),
                branch_id: parseInt(branchId),
              },
            });
            if (data?.getIngredientStock) {
              const total = data.getIngredientStock.reduce(
                (sum: number, loc: any) => sum + Number(loc.quantity),
                0
              );
              newLevels[inp.ingredient_id] = total;
            }
          } catch {
            newLevels[inp.ingredient_id] = 0;
          }
        }
      );
      Promise.all(promises).then(() => setStockLevels(newLevels));
    }
    //eslint-disable-next-line
  }, [selectedRecipeId, branchId]);

  const handleStart = () => {
    if (!selectedRecipeId || !branchId) {
      toast.error(
        lang(
          "Selecciona receta y sucursal",
          "Select recipe and branch"
        )
      );
      return;
    }
    startBatch({
      variables: {
        recipe_id: parseInt(selectedRecipeId),
        branch_id: parseInt(branchId),
        notes: notes || null,
      },
    });
  };

  if (loadingRecipes) return <LoaderDots fullSize />;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {lang("Iniciar Producción", "Start Production")}
        </h4>
        <button
          className="btn btn-outline-secondary"
          onClick={() => history.push("/inventory/production")}
        >
          {lang("Volver", "Back")}
        </button>
      </div>

      <div className="material_card p-3 mb-3">
        <div className="row">
          <div className="col-lg-6">
            <div className="form-group">
              <label>{lang("Receta de Producción", "Production Recipe")} *</label>
              <select
                className="custom-select"
                value={selectedRecipeId}
                onChange={(e) => setSelectedRecipeId(e.target.value)}
              >
                <option value="">
                  {lang("Seleccionar receta...", "Select recipe...")}
                </option>
                {recipeData?.getProductionRecipes?.map(
                  (r: ProductionRecipe) => (
                    <option key={r.id} value={r.id}>
                      {r.name} → {r.output_ingredient?.name || "?"} (
                      {Number(r.output_quantity)} {r.output_unit})
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="form-group">
              <label>{lang("Sucursal", "Branch")} *</label>
              <select
                className="custom-select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              >
                <option value="">
                  {lang("Seleccionar...", "Select...")}
                </option>
                {branchData?.getBranches?.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <div className="form-group">
              <label>{lang("Notas", "Notes")}</label>
              <textarea
                className="form-control"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {selectedRecipe && (
        <div className="material_card p-3 mb-3">
          <h5>{lang("Insumos Requeridos", "Required Inputs")}</h5>
          <table className="table table-sm vertical_middle">
            <thead className="thead-light">
              <tr>
                <th>{lang("Ingrediente", "Ingredient")}</th>
                <th className="text-center">
                  {lang("Requerido", "Required")}
                </th>
                <th className="text-center">
                  {lang("Stock Actual", "Current Stock")}
                </th>
                <th className="text-center">{lang("Estado", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {selectedRecipe.inputs.map(
                (inp: ProductionRecipeInputType) => {
                  const currentStock =
                    stockLevels[inp.ingredient_id] ?? null;
                  const required = Number(inp.quantity);
                  const insufficient =
                    currentStock !== null && currentStock < required;

                  return (
                    <tr key={inp.id}>
                      <td>
                        {inp.ingredient?.name || `ID ${inp.ingredient_id}`}
                        <small className="text-muted ml-1">
                          ({inp.unit})
                        </small>
                      </td>
                      <td className="text-center">
                        {required} {inp.unit}
                      </td>
                      <td className="text-center">
                        {branchId && currentStock !== null
                          ? `${currentStock} ${
                              inp.ingredient?.unit || inp.unit
                            }`
                          : "—"}
                      </td>
                      <td className="text-center">
                        {!branchId ? (
                          <span className="text-muted">—</span>
                        ) : currentStock === null ? (
                          <i className="fa fa-spinner fa-spin text-muted" />
                        ) : insufficient ? (
                          <span className="badge badge-danger">
                            {lang("Insuficiente", "Insufficient")}
                          </span>
                        ) : (
                          <span className="badge badge-success">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>

          <div className="mt-3 p-2 bg-light rounded">
            <strong>{lang("Resultado esperado:", "Expected output:")}</strong>{" "}
            {Number(selectedRecipe.output_quantity)}{" "}
            {selectedRecipe.output_unit}{" "}
            {lang("de", "of")}{" "}
            <strong>{selectedRecipe.output_ingredient?.name}</strong>
          </div>
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleStart}
        disabled={starting || !selectedRecipeId || !branchId}
      >
        {starting ? (
          <i className="fa fa-spinner fa-spin mr-1" />
        ) : (
          <i className="fa fa-play mr-1" />
        )}
        {lang("Iniciar Producción", "Start Production")}
      </button>
    </>
  );
};

export default StartBatch;
